import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const relatedArticles = article ? getRelatedArticles(article, 3) : [];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 51吃瓜大赛`;
    }
    window.scrollTo(0, 0);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">文章不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '51吃瓜大赛',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://hcenish.cn/' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `http://hcenish.cn/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `http://hcenish.cn/article/${article.id}` },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
        />

        <article>
          <div className="w-full bg-background-100 py-8 md:py-12">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors cursor-pointer">{article.category}</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-800 line-clamp-1">{article.title}</span>
              </nav>

              <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 leading-snug mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万次阅读` : `${article.views.toLocaleString()}次阅读`}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i>
                  {article.comments.toLocaleString()} 评论
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 bg-background-50 text-foreground-600 text-xs rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full py-8 md:py-12 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="flex-1">
                  <div className="rounded-xl overflow-hidden mb-8">
                    <img
                      src={article.imageUrl}
                      alt={article.imageAlt}
                      className="w-full aspect-[16/9] object-cover"
                    />
                  </div>

                  <div
                    className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                      [&_h2]:text-lg [&_h2]:md:text-xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                      [&_p]:mb-4 [&_p]:text-sm [&_p]:md:text-base [&_p]:leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />
                </div>

                {relatedArticles.length > 0 && (
                  <aside className="lg:w-72 flex-shrink-0">
                    <div className="lg:sticky lg:top-24">
                      <h3 className="text-sm font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                        <i className="ri-links-line text-primary-500"></i>
                        相关吃瓜推荐
                      </h3>
                      <div className="space-y-4">
                        {relatedArticles.map((related) => (
                          <Link
                            key={related.id}
                            to={`/article/${related.id}`}
                            className="flex gap-3 group cursor-pointer"
                          >
                            <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                              <img
                                src={related.imageUrl}
                                alt={related.imageAlt}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="text-[10px] text-primary-600 font-medium">{related.category}</span>
                              <h4 className="text-xs font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mt-0.5">
                                {related.title}
                              </h4>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </aside>
                )}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}